import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { WarehouseID } from "@models/Address";
import { PurchaseOrderVariant } from "@models/PurchaseOrderVariant";
import client, { useMutateError } from "@services/api";
import { updateWithToast } from "@services/toasts";
import asyncPool from "@utility/asyncPool";
import { buildPaginatedQuery } from "@utils/reactQuery";

export const purchaseOrderVariantsKeyFactory = createQueryKeys(
  "purchaseOrderVariants",
  {
    list: (purchaseOrderId: string) => ({
      queryKey: [purchaseOrderId],
      queryFn: () =>
        client
          .get<PurchaseOrderVariant[]>(`/purchase-order-variants`, {
            params: {
              filter: { purchaseOrderId },
              skipPagination: true,
            },
          })
          .then((res) => res.data),
    }),
    paginated: (params) => ({
      queryKey: [params],
      queryFn: () =>
        client.get<PurchaseOrderVariant[]>(`/purchase-order-variants`, {
          params,
        }),
    }),
  }
);

export const usePurchaseOrderVariantsListQuery = (
  purchaseOrderId?: string | null
) => {
  return useQuery({
    ...purchaseOrderVariantsKeyFactory.list(purchaseOrderId!),
    enabled: !!purchaseOrderId,
  });
};

export const usePaginatePurchaseOrderVariantsQuery = buildPaginatedQuery(
  purchaseOrderVariantsKeyFactory.paginated,
  {
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  }
);

type CreatePurchaseOrderVariantPayload = {
  purchaseOrderId: string;
  variantQtys: {
    id: string; // variant id
    qty: number;
  }[];
  warehouse: WarehouseID;
};

// Only used to create inventory purchase order variants ie. "restock"
export const useCreatePurchaseOrderVariantMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      purchaseOrderId,
      warehouse,
      variantQtys,
    }: CreatePurchaseOrderVariantPayload) => {
      async function createVariant({ id, qty }) {
        return client
          .post<PurchaseOrderVariant>(`/purchase-order-variants`, {
            __type: "purchase-order-variant",
            warehouse,
            variantId: +id,
            qty,
            purchaseOrderId: +purchaseOrderId,
            isToInventory: true,
          })
          .then((res) => res.data);
      }
      const [firstQty, ...rest] = variantQtys;
      // according to Shaun, we need to create the first variant synchronously, the rest can be done in parallel
      // otherwise it creates race conditions in the back-end
      const firstPov = await createVariant(firstQty);
      const { errors, results } = await asyncPool(5, rest, createVariant);

      if (errors) {
        throw errors[0];
      }
      return [firstPov, ...results];
    },
    onError: (error) => setError(error),
    onSuccess: (newPovs, { purchaseOrderId }) => {
      queryClient.invalidateQueries({
        queryKey: purchaseOrderVariantsKeyFactory.paginated._def,
      });
      return queryClient.setQueryData<PurchaseOrderVariant[]>(
        purchaseOrderVariantsKeyFactory.list(purchaseOrderId).queryKey,
        (oldData) => oldData && [...oldData, ...newPovs]
      );
    },
  });
};

type SetupFeePayload = {
  purchaseOrderId: string;
  setupFeeDesc: string;
  actualCost: number;
};

export const useCreateSetupFeeMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SetupFeePayload) =>
      client
        .post<PurchaseOrderVariant>(`/purchase-order-variants`, {
          __type: "purchase-order-variant",
          ...payload,
          purchaseOrderId: +payload.purchaseOrderId,
          isSetupFee: true,
        })
        .then((res) => res.data),
    onError: (error) => setError(error),
    onSuccess: (data, { purchaseOrderId }) => {
      queryClient.invalidateQueries({
        queryKey: purchaseOrderVariantsKeyFactory.paginated._def,
      });
      queryClient.setQueryData<PurchaseOrderVariant[]>(
        purchaseOrderVariantsKeyFactory.list(purchaseOrderId).queryKey,
        (oldData) => oldData && [...oldData, data]
      );
    },
  });
};

type UpdatePurchaseOrderVariantPayload = {
  id: string;
  purchaseOrderId: string;
  actualCost?: string;
  setupFeeDesc?: string;
};

export const useUpdatePurchaseOrderVariantMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }: UpdatePurchaseOrderVariantPayload) =>
      updateWithToast(
        client
          .patch<PurchaseOrderVariant>(`/purchase-order-variants/${id}`, {
            __type: "purchase-order-variant",
            ...payload,
          })
          .then((res) => res.data)
      ),
    onError: (error) => setError(error),
    onSuccess: (data, { purchaseOrderId }) => {
      queryClient.invalidateQueries({
        queryKey: purchaseOrderVariantsKeyFactory.paginated._def,
      });
      queryClient.setQueryData<PurchaseOrderVariant[]>(
        purchaseOrderVariantsKeyFactory.list(purchaseOrderId).queryKey,
        (oldData) =>
          oldData && oldData.map((v) => (v.id === data.id ? data : v))
      );
    },
  });
};

type DeletePurchaseOrderVariantPayload = {
  id: string;
  purchaseOrderId: string;
};

export const useDeletePurchaseOrderVariantMutation = () => {
  const setError = useMutateError();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: DeletePurchaseOrderVariantPayload) =>
      client.delete(`/purchase-order-variants/${id}`),
    onError: (error) => setError(error),
    onSuccess: (_, { id, purchaseOrderId }) => {
      queryClient.invalidateQueries({
        queryKey: purchaseOrderVariantsKeyFactory.paginated._def,
      });
      queryClient.setQueryData<PurchaseOrderVariant[]>(
        purchaseOrderVariantsKeyFactory.list(purchaseOrderId).queryKey,
        (oldData) => oldData && oldData.filter((v) => v.id !== id)
      );
    },
  });
};
